/**
 * useFileUpload — Shared upload logic for ImageUpload, VideoUpload, AudioUpload nodes.
 *
 * Handles:
 * - File validation (type + per-type size limits: image 10MB, video 300MB, audio 50MB)
 * - Object URL lifecycle (create, revoke on replace, revoke on unmount)
 * - Upload via backend API (POST /projects/{id}/assets/upload)
 * - Status transitions (idle -> running -> success/error)
 * - Drag-and-drop support
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '../../../../api/client'
import type { UploadSource } from '../../../../analytics/generated/analyticsEvents'
import { uploadSourceOf } from '../../../../analytics/uploadSource'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { createNadDebug } from '../../../../utils/debugLog'
import { MAX_FILE_SIZE, getFileSizeError } from '../../../../utils/uploadFileValidation'
import type { NodeDataRecord } from '../../nodeRegistry'
import { encodeNodeError } from './nodeErrorCode'
import { createUploadAssetRef } from './uploadAssetRef'

type UploadNodeType = 'audio' | 'image' | 'video'

export { MAX_FILE_SIZE, getFileSizeError }

const uploadValidationLog = createNadDebug('dw:asset-upload:validation')

/** Map media type to the schema output field name (out_image, out_video, out_audio) */
const MEDIA_TO_OUT_FIELD: Record<string, string> = {
  image: 'out_image',
  video: 'out_video',
  audio: 'out_audio',
}

interface UseFileUploadOptions<T extends UploadNodeType> {
  acceptMime: T
  nodeId?: string
  updateNodeData: (updates: NodeDataRecord<T>) => void
  /** The persisted out_* value from YDoc (e.g. data.out_image).
   *  When this becomes truthy, the blob preview URL is revoked. */
  persistedValue?: unknown
  /** Eagerly-created blob URL for instant preview (e.g. from Director upload).
   *  Seeds previewUrl and isUploading state so the first render shows the
   *  uploading state with a preview instead of the empty upload area. */
  initialPreviewUrl?: string
}

interface UseFileUploadReturn {
  /** Current preview URL (object URL or resolved asset URL) */
  previewUrl: string | null
  /** Whether an upload is currently in progress */
  isUploading: boolean
  /** Current error message, if any */
  error: string | null
  /** Handle file input change */
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** Drag-and-drop event handlers */
  dragHandlers: {
    onDragOver: (e: React.DragEvent) => void
    onDragLeave: (e: React.DragEvent) => void
    onDrop: (e: React.DragEvent) => void
  }
  /** Whether the drag zone is currently highlighted */
  isDragOver: boolean
  /** Retry the last failed upload. Only meaningful when error is set. */
  retry: () => void
  /** Clear the upload state (for Remove button) */
  clear: () => void
  /** Temporary warning shown when multiple files are dropped */
  multiFileWarning: string | null
  /** Programmatically upload a file (for Director upload and canvas-level drop).
   *  Its analytics source is the one the creating surface tagged on the File. */
  uploadFile: (file: File) => void
}

export function useFileUpload<T extends UploadNodeType>({
  acceptMime,
  nodeId,
  updateNodeData,
  persistedValue,
  initialPreviewUrl,
}: UseFileUploadOptions<T>): UseFileUploadReturn {
  const { t } = useTranslation('canvas')
  const projectId = useProjectId()
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialPreviewUrl ?? null)
  const [isUploading, setIsUploading] = useState(!!initialPreviewUrl)
  const [error, setError] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [multiFileWarning, setMultiFileWarning] = useState<string | null>(null)
  const objectUrlRef = useRef<string | null>(initialPreviewUrl ?? null)
  const lastFileRef = useRef<File | null>(null)
  const lastSourceRef = useRef<UploadSource | undefined>(undefined)

  // Once YDoc confirms the persisted value, revoke the blob preview URL.
  // This avoids the flash of empty state that would occur if we cleared
  // previewUrl eagerly in processFile (YDoc propagation is async).
  useEffect(() => {
    if (persistedValue && objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
      setPreviewUrl(null)
    }
  }, [persistedValue])

  // Cleanup refs on unmount.
  // We intentionally do NOT revoke objectUrlRef here — React 18 StrictMode
  // simulates unmount/remount, which would revoke the blob URL while an
  // upload is still in progress (breaking the spinner preview). Blob URLs
  // are explicitly revoked on success, error, clear, and replacement;
  // any remaining ones are freed automatically on page unload.
  useEffect(() => {
    return () => {
      objectUrlRef.current = null
      lastFileRef.current = null
    }
  }, [])

  const processFile = useCallback(
    async (file: File, source: UploadSource | undefined = uploadSourceOf(file)) => {
      // Validate MIME type. Persist a structured code (not localized prose)
      // so the error is localized at render time (systemic rec #2).
      if (!file.type.startsWith(`${acceptMime}/`)) {
        const invalidType = encodeNodeError({
          code: 'upload.invalidType',
          params: { accept: acceptMime },
        })
        setError(invalidType)
        setIsUploading(false)
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current)
          objectUrlRef.current = null
          setPreviewUrl(null)
        }
        updateNodeData({ status: 'error', error: invalidType })
        return
      }

      // Validate file size (per-type limit)
      const sizeError = getFileSizeError(file.size, acceptMime)
      if (sizeError) {
        const encoded = encodeNodeError(sizeError)
        uploadValidationLog({
          event: 'file-size-rejected',
          node_id: nodeId,
          project_id: projectId,
          media_type: acceptMime,
          file_size_bytes: file.size,
          max_size_bytes: MAX_FILE_SIZE[acceptMime],
        })
        setError(encoded)
        setIsUploading(false)
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current)
          objectUrlRef.current = null
          setPreviewUrl(null)
        }
        updateNodeData({ status: 'error', error: encoded })
        return
      }

      uploadValidationLog({
        event: 'upload-started',
        project_id: projectId,
        node_id: nodeId,
        media_type: acceptMime,
        file_size_bytes: file.size,
      })
      setError(null)
      setIsUploading(true)

      // Retain file for retry on failure
      lastFileRef.current = file
      lastSourceRef.current = source

      // Revoke previous object URL
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
        objectUrlRef.current = null
      }

      // Create object URL for immediate preview
      const url = URL.createObjectURL(file)
      objectUrlRef.current = url
      setPreviewUrl(url)

      const outField = MEDIA_TO_OUT_FIELD[acceptMime] || `out_${acceptMime}`
      updateNodeData({
        status: 'running',
        in_file_name: file.name,
        error: undefined,
      } as NodeDataRecord<T>)

      // Upload via backend API (write-through to Supabase + local cache)
      try {
        const result = await api.uploadAsset(projectId, file, undefined, source)

        // Store the asset history wrapper in the out_* field (YDoc-persisted).
        // Display URL is derived at render time via api.getAssetUrl().
        // Keep the blob preview URL alive — clearing it now would cause a
        // flash of empty state because out_image propagates through YDoc
        // asynchronously and arrives a frame later. The component clears
        // previewUrl once it sees out_image from YDoc (see useEffect below).
        updateNodeData({
          status: 'completed',
          [outField]: createUploadAssetRef(result.path, {
            aspectRatio: result.aspect_ratio,
            width: result.width,
            height: result.height,
            durationSeconds: result.duration_seconds,
          }),
          ...(acceptMime === 'video'
            ? { out_video_duration: result.visual_duration_seconds ?? undefined }
            : {}),
          _initialPreviewUrl: undefined,
        } as NodeDataRecord<T>)

        uploadValidationLog({
          event: 'upload-completed',
          project_id: projectId,
          node_id: nodeId,
          media_type: acceptMime,
          status: 'completed',
        })
        lastFileRef.current = null
        setIsUploading(false)
      } catch (err) {
        // A concrete Error message is backend/runtime prose — persist it
        // verbatim (it passes through the render-time resolver unchanged; see
        // #2430 for backend-authoritative localization). Only the generic
        // client fallback is a structured, render-localized code.
        const msg =
          err instanceof Error && err.message
            ? err.message
            : encodeNodeError({ code: 'upload.failed' })
        setError(msg)
        updateNodeData({ status: 'error', error: msg })
        setIsUploading(false)

        // Revoke object URL on error
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current)
          objectUrlRef.current = null
          setPreviewUrl(null)
        }
      }
    },
    [acceptMime, nodeId, updateNodeData, projectId]
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      processFile(file, 'picker')
    },
    [processFile]
  )

  const dragHandlers = {
    onDragOver: useCallback((e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(true)
    }, []),
    onDragLeave: useCallback((e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)
    }, []),
    onDrop: useCallback(
      (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
        const files = e.dataTransfer.files
        if (files.length > 1) {
          // Transient (not persisted to YDoc) — localize directly.
          setMultiFileWarning(t('nodeError.upload.oneFilePerNode'))
          setTimeout(() => setMultiFileWarning(null), 3000)
        }
        const file = files[0]
        if (file) processFile(file, 'drag_drop')
      },
      [processFile, t]
    ),
  }

  const retry = useCallback(() => {
    if (lastFileRef.current) {
      processFile(lastFileRef.current, lastSourceRef.current)
    }
  }, [processFile])

  const clear = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }
    lastFileRef.current = null
    setPreviewUrl(null)
    setIsUploading(false)
    setError(null)
    setMultiFileWarning(null)
    const outField = MEDIA_TO_OUT_FIELD[acceptMime] || `out_${acceptMime}`
    updateNodeData({
      status: 'idle',
      [outField]: undefined,
      ...(acceptMime === 'video' ? { out_video_duration: undefined } : {}),
      in_file_name: undefined,
      error: undefined,
    } as NodeDataRecord<T>)
  }, [acceptMime, updateNodeData])

  // NOTE: Auto-upload of initialFile is NOT handled here because the
  // updateNodeData callback may capture a stale canvas context on first mount.
  // Each upload node component handles initialFile via its own useEffect that
  // waits for the canvas context to be available before calling uploadFile().

  return {
    previewUrl,
    isUploading,
    error,
    handleFileChange,
    dragHandlers,
    isDragOver,
    retry,
    clear,
    multiFileWarning,
    uploadFile: processFile,
  }
}

/**
 * Format file size for display.
 * Shows KB for < 1MB, MB for >= 1MB.
 */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
  return `${(bytes / 1024).toFixed(1)} KB`
}
